(function initializeCartRecovery(global) {
  "use strict";

  const RECOVERY_PARAMETER = "tpp_cart_recovery";
  const RECOVERY_LANDING_PATH = "pages/cart-recovery";
  const RECOVERY_EVENT = "Recoverable Cart Updated";
  const MUTATION_PATHS = ["/cart/add", "/cart/change", "/cart/update", "/cart/clear"];
  const CAPTURE_DELAY_MS = 400;
  const PENDING_CAPTURE_KEY = "tpp_cart_capture_pending";
  const PENDING_CART_KEY = "tpp_cart_capture_cart";
  const PENDING_CART_MARKER_KEY = "tpp_cart_capture_cart_marker";
  const PENDING_CART_RESTORE_MARKER_KEY = "tpp_cart_capture_restore_marker";
  const CURRENT_CART_KEY = "tpp_cart_current_cart";
  const LATEST_CAPTURE_KEY = "tpp_cart_capture_latest";
  const LATEST_RESTORE_KEY = "tpp_cart_restore_latest";
  const DISCOUNT_CODES_KEY = "tpp_cart_discount_codes";
  const LATEST_DISCOUNT_MUTATION_KEY = "tpp_cart_discount_mutation_latest";
  const LATEST_SUCCESSFUL_DISCOUNT_MUTATION_KEY = "tpp_cart_discount_mutation_success_latest";
  const LATEST_RECOVERY_KEY = "tpp_cart_recovery_latest";
  const LATEST_TRACKED_CART_KEY = "tpp_cart_klaviyo_latest";
  const CAPTURE_STATE_LOCK = "tpp-cart-recovery-capture-state";
  const DISCOUNT_MUTATION_LOCK = "tpp-cart-recovery-discount-mutation";
  const CART_RESTORE_LOCK = "tpp-cart-recovery-restore";
  const PENDING_CART_MAX_AGE_MS = 5 * 60 * 1000;
  const CAPTURE_RETRY_DELAYS_MS = [1000, 3000, 10000];
  const POST_MUTATION_CART_TIMEOUT_MS = 500;
  const KLAVIYO_TRACK_TIMEOUT_MS = 5000;
  const CHECKOUT_CAPTURE_TIMEOUT_MS = 2000;
  const CART_CAPTURE_REQUEST_TIMEOUT_MS = 5000;
  const CART_RESTORE_REQUEST_TIMEOUT_MS = 10000;
  const CART_MUTATION_REQUEST_TIMEOUT_MS = 10000;
  const FALLBACK_LOCK_LEASE_MS = 30000;
  const FALLBACK_LOCK_RETRY_MS = 25;
  const FALLBACK_LOCK_PREFIX = "tpp_cart_lock:";
  const KEEPALIVE_BODY_MAX_BYTES = 60 * 1024;
  const RECOVERY_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;
  const ACCELERATED_CHECKOUT_TAGS = new Set([
    "shop-pay-wallet-button",
    "shopify-accelerated-checkout",
    "shopify-accelerated-checkout-cart",
    "shopify-amazon-pay-button",
    "shopify-apple-pay-button",
    "shopify-google-pay-button",
    "shopify-paypal-button",
  ]);
  const CAPTURE_INSTANCE_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let captureTimer = null;
  let captureGeneration = 0;
  let captureMarkerCounter = 0;
  let discountMutationCounter = 0;
  let latestCaptureMarker = "";
  let latestDiscountMutationMarker = "";
  let latestSuccessfulDiscountMutationMarker = "";
  let validatedTrackedCartToken = "";
  let restoring = false;
  let checkoutNavigationPending = false;
  let acceleratedCheckoutFlushPromise = null;
  let acceleratedCheckoutNavigationDeferred = false;
  let acceleratedCheckoutResumeBypass = false;
  let cartMutationGeneration = 0;
  let fallbackLockCounter = 0;
  const activeCartMutations = new Set();
  const fallbackLockTails = new Map();

  function route(path) {
    const root = String(global.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    return `${root}${String(path).replace(/^\//, "")}`;
  }

  function requestPath(input) {
    try {
      const rawUrl = typeof input === "string" ? input : input?.url || input?.href || input;
      return new URL(rawUrl, global.location.origin).pathname.replace(/\.js$/, "");
    } catch (_error) {
      return "";
    }
  }

  function isCartMutation(input, options = {}) {
    const method = String(options.method || input?.method || "GET").toUpperCase();
    if (method === "GET") return false;
    try {
      const rawUrl = typeof input === "string" ? input : input?.url || input?.href || input;
      const url = new URL(rawUrl, global.location.origin);
      if (url.origin !== global.location.origin) return false;
      const path = url.pathname.replace(/\.js$/, "");
      return MUTATION_PATHS.some((cartPath) => {
        const localizedPath = new URL(route(cartPath), global.location.origin).pathname;
        return path === cartPath || path === localizedPath;
      });
    } catch (_error) {
      return false;
    }
  }

  function copyProperties(properties) {
    if (!properties || typeof properties !== "object" || Array.isArray(properties)) return {};
    return Object.fromEntries(Object.entries(properties).filter(([, value]) =>
      ["string", "number", "boolean"].includes(typeof value),
    ));
  }

  function positiveInteger(value) {
    const normalized = Number.parseInt(value, 10);
    return Number.isSafeInteger(normalized) && normalized > 0 ? normalized : null;
  }

  function normalizeDiscountCodes(value) {
    const entries = Array.isArray(value) ? value : String(value || "").split(",");
    return [...new Set(entries.map((entry) =>
      String((entry?.code ?? entry) || "").trim(),
    ).filter(Boolean))];
  }

  function storedDiscountCodes(cartToken) {
    try {
      const stored = JSON.parse(global.localStorage?.getItem(DISCOUNT_CODES_KEY) || "null");
      const token = String(cartToken || "");
      const storedToken = String(stored?.cart_token || "");
      if (!stored || Array.isArray(stored) || !storedToken) {
        global.localStorage?.removeItem(DISCOUNT_CODES_KEY);
        return [];
      }
      if (storedToken !== token) return [];
      return normalizeDiscountCodes(stored.codes);
    } catch (_error) {
      return [];
    }
  }

  function setStoredDiscountCodes(codes, cartToken) {
    try {
      const normalized = normalizeDiscountCodes(codes);
      const token = String(cartToken || "");
      if (normalized.length && token) {
        global.localStorage?.setItem(DISCOUNT_CODES_KEY, JSON.stringify({
          cart_token: token,
          codes: normalized,
        }));
      } else {
        global.localStorage?.removeItem(DISCOUNT_CODES_KEY);
      }
      global.sessionStorage?.removeItem(DISCOUNT_CODES_KEY);
    } catch (_error) {}
  }

  function fallbackLockDelay() {
    return new Promise((resolve) => {
      const timer = global.setTimeout?.(resolve, FALLBACK_LOCK_RETRY_MS);
      // Test doubles and restricted embeds sometimes expose a no-op timer.
      // Yield through the promise queue rather than deadlocking forever.
      if (timer === undefined) Promise.resolve().then(resolve);
    });
  }

  function fallbackLockRecord(storage, key) {
    try {
      return JSON.parse(storage.getItem(key) || "null");
    } catch (_error) {
      return null;
    }
  }

  async function withStorageLockLease(name, callback) {
    const storage = global.localStorage;
    if (
      typeof storage?.getItem !== "function" ||
      typeof storage?.setItem !== "function" ||
      typeof storage?.removeItem !== "function"
    ) return callback();

    const key = `${FALLBACK_LOCK_PREFIX}${name}`;
    const owner = `${CAPTURE_INSTANCE_ID}-${++fallbackLockCounter}`;
    let acquired = false;
    try {
      while (!acquired) {
        const now = Date.now();
        const current = fallbackLockRecord(storage, key);
        if (!current?.owner || Number(current.expires_at) <= now) {
          storage.setItem(key, JSON.stringify({
            owner,
            expires_at: now + FALLBACK_LOCK_LEASE_MS,
          }));
          acquired = fallbackLockRecord(storage, key)?.owner === owner;
          if (acquired) break;
        }
        await fallbackLockDelay();
      }
    } catch (_error) {
      // The same-document queue still provides mutual exclusion when storage
      // is unavailable or blocked by browser privacy settings.
      return callback();
    }

    let heartbeat;
    if (typeof global.setInterval === "function") {
      heartbeat = global.setInterval(() => {
        try {
          if (fallbackLockRecord(storage, key)?.owner !== owner) return;
          storage.setItem(key, JSON.stringify({
            owner,
            expires_at: Date.now() + FALLBACK_LOCK_LEASE_MS,
          }));
        } catch (_error) {}
      }, Math.floor(FALLBACK_LOCK_LEASE_MS / 3));
    }
    try {
      return await callback();
    } finally {
      if (heartbeat !== undefined) global.clearInterval?.(heartbeat);
      try {
        if (fallbackLockRecord(storage, key)?.owner === owner) {
          storage.removeItem(key);
        }
      } catch (_error) {}
    }
  }

  function withFallbackLock(name, callback) {
    const previous = fallbackLockTails.get(name) || Promise.resolve();
    let release;
    const tail = new Promise((resolve) => { release = resolve; });
    fallbackLockTails.set(name, tail);
    return previous.catch(() => {}).then(() =>
      withStorageLockLease(name, callback),
    ).finally(() => {
      release();
      if (fallbackLockTails.get(name) === tail) fallbackLockTails.delete(name);
    });
  }

  function withCrossTabLock(name, callback, mode = "exclusive") {
    const locks = global.navigator?.locks;
    if (typeof locks?.request === "function") {
      return locks.request(name, { mode }, callback);
    }
    // localStorage does not support shared leases, so serialize shared readers
    // conservatively with writers when Web Locks are unavailable.
    return withFallbackLock(name, callback);
  }

  function markerOrder(marker) {
    return Number.parseInt(String(marker || "").split("-", 1)[0], 10) || 0;
  }

  function compareOrderedMarkers(leftMarker, rightMarker) {
    const left = String(leftMarker || "");
    const right = String(rightMarker || "");
    const leftOrder = markerOrder(left);
    const rightOrder = markerOrder(right);
    if (leftOrder !== rightOrder) return leftOrder > rightOrder ? 1 : -1;
    return 0;
  }

  function markLatestDiscountMutation() {
    return withCrossTabLock(DISCOUNT_MUTATION_LOCK, () => {
      let previousMarker = latestDiscountMutationMarker;
      try {
        previousMarker = global.localStorage?.getItem(LATEST_DISCOUNT_MUTATION_KEY) || previousMarker;
      } catch (_error) {}
      const order = Math.max(Date.now() * 1000, markerOrder(previousMarker) + 1);
      const marker = `${order}-${CAPTURE_INSTANCE_ID}-${++discountMutationCounter}`;
      latestDiscountMutationMarker = marker;
      try {
        global.localStorage?.setItem(LATEST_DISCOUNT_MUTATION_KEY, marker);
      } catch (_error) {}
      return marker;
    });
  }

  function storeSuccessfulDiscountMutation(marker, codes, cartToken) {
    return withCrossTabLock(DISCOUNT_MUTATION_LOCK, () => {
      try {
        const latestMarker = global.localStorage?.getItem(
          LATEST_SUCCESSFUL_DISCOUNT_MUTATION_KEY,
        ) || latestSuccessfulDiscountMutationMarker;
        if (!marker || compareOrderedMarkers(marker, latestMarker) <= 0) return false;
        latestSuccessfulDiscountMutationMarker = marker;
        global.localStorage?.setItem(LATEST_SUCCESSFUL_DISCOUNT_MUTATION_KEY, marker);
      } catch (_error) {
        if (!marker || compareOrderedMarkers(
          marker,
          latestSuccessfulDiscountMutationMarker,
        ) <= 0) return false;
        latestSuccessfulDiscountMutationMarker = marker;
      }
      // A successful response may still have an unreadable body, and the
      // fallback cart read can fail independently. Keep the last token-scoped
      // discount rather than interpreting an unavailable token as a removal.
      if (cartToken) setStoredDiscountCodes(codes, cartToken);
      return Boolean(cartToken);
    });
  }

  function cartDiscountCodes(cart, includeStoredFallback = true) {
    const hasOwn = (name) => Object.prototype.hasOwnProperty.call(cart || {}, name);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const directDiscounts = cart?.discount_codes ?? cart?.discountCodes;
    const directEntries = Array.isArray(directDiscounts)
      ? directDiscounts
      : String(directDiscounts || "").split(",");
    const applications = [
      ...directEntries.filter((entry) => entry && typeof entry === "object").map((entry) => ({
        type: "discount_code",
        ...entry,
      })),
      ...(Array.isArray(cart?.discount_applications) ? cart.discount_applications : []),
      ...(Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications : []),
      ...items.flatMap((item) =>
        (Array.isArray(item?.line_level_discount_allocations)
          ? item.line_level_discount_allocations : []).map((allocation) =>
          allocation?.discount_application,
        )),
    ];
    const applicableCodes = applications.map((application) => {
      const type = String(application?.type || "").toLowerCase().replace(/[^a-z]/g, "");
      if (type !== "discountcode" || application?.applicable === false) return "";
      return application?.code || application?.title || "";
    });
    const inapplicableCodes = new Set(applications.map((application) => {
      const type = String(application?.type || "").toLowerCase().replace(/[^a-z]/g, "");
      if (type !== "discountcode" || application?.applicable !== false) return "";
      return String(application?.code || application?.title || "").trim().toLowerCase();
    }).filter(Boolean));
    const hasDirectCodes = hasOwn("discount_codes") || hasOwn("discountCodes");
    const baseCodes = hasDirectCodes
      ? normalizeDiscountCodes(directEntries.filter((entry) => entry?.applicable !== false))
      : includeStoredFallback ? storedDiscountCodes(cart?.token) : [];
    return normalizeDiscountCodes([
      ...baseCodes.filter((code) => !inapplicableCodes.has(code.toLowerCase())),
      ...applicableCodes,
    ]);
  }

  function authoritativeDiscountCodes(cart) {
    const hasOwn = (name) => Object.prototype.hasOwnProperty.call(cart || {}, name);
    if (hasOwn("discount_codes") || hasOwn("discountCodes")) {
      return cartDiscountCodes(cart, false);
    }
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const applications = [
      ...(Array.isArray(cart?.discount_applications) ? cart.discount_applications : []),
      ...(Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications : []),
      ...items.flatMap((item) =>
        (Array.isArray(item?.line_level_discount_allocations)
          ? item.line_level_discount_allocations : []).map((allocation) =>
          allocation?.discount_application,
        )),
    ];
    const hasDiscountCodeResult = applications.some((application) =>
      String(application?.type || "").toLowerCase().replace(/[^a-z]/g, "") === "discountcode",
    );
    return hasDiscountCodeResult ? cartDiscountCodes(cart, false) : null;
  }

  function latestRecovery() {
    try {
      const stored = JSON.parse(global.localStorage?.getItem(LATEST_RECOVERY_KEY) || "null");
      const recoveryId = String(stored?.recovery_id || "");
      return recoveryId ? {
        recoveryId,
        cartToken: String(stored?.cart_token || ""),
        captureMarker: String(stored?.capture_marker || ""),
      } : null;
    } catch (_error) {
      return null;
    }
  }

  function setLatestRecovery(recoveryId, cartToken, captureMarker) {
    try {
      global.localStorage?.setItem(LATEST_RECOVERY_KEY, JSON.stringify({
        recovery_id: String(recoveryId || ""),
        cart_token: String(cartToken || ""),
        capture_marker: String(captureMarker || ""),
      }));
    } catch (_error) {}
  }

  function clearLatestRecovery(recoveryId, captureMarker = "") {
    try {
      const latest = latestRecovery();
      if (
        latest?.recoveryId === String(recoveryId || "") &&
        (!captureMarker || latest.captureMarker === String(captureMarker))
      ) {
        global.localStorage?.removeItem(LATEST_RECOVERY_KEY);
      }
    } catch (_error) {}
  }

  function latestTrackedCart(cartToken = "") {
    try {
      const stored = JSON.parse(global.localStorage?.getItem(LATEST_TRACKED_CART_KEY) || "null");
      if (!stored || !Array.isArray(stored.items)) return null;
      const storedToken = String(stored.cart_token || "");
      if (cartToken && storedToken !== String(cartToken)) return null;
      return {
        recoveryId: String(stored.recovery_id || ""),
        cartToken: storedToken,
        captureMarker: String(stored.capture_marker || ""),
        itemCount: Number(stored.item_count) || 0,
        totalPrice: Number(stored.total_price) || 0,
        discountCodes: normalizeDiscountCodes(stored.discount_codes),
        note: String(stored.note || ""),
        attributes: copyProperties(stored.attributes),
        items: stored.items,
      };
    } catch (_error) {
      return null;
    }
  }

  function setLatestTrackedCart(cart, recoveryId, captureMarker) {
    try {
      global.localStorage?.setItem(LATEST_TRACKED_CART_KEY, JSON.stringify({
        recovery_id: String(recoveryId || ""),
        cart_token: String(cart?.token || ""),
        capture_marker: String(captureMarker || ""),
        item_count: Number(cart?.item_count) || 0,
        total_price: Number(cart?.total_price) || 0,
        discount_codes: cartDiscountCodes(cart),
        note: String(cart?.note || ""),
        attributes: copyProperties(cart?.attributes),
        items: klaviyoItems(cart),
      }));
      validatedTrackedCartToken = String(cart?.token || "");
    } catch (_error) {}
    storeCurrentCartSnapshot(cart);
  }

  function mutationCartState(cart) {
    return {
      cartToken: String(cart?.token || cart?.cartToken || ""),
      itemCount: Number(cart?.item_count ?? cart?.itemCount) || 0,
      discountCodes: cartDiscountCodes(cart),
      note: String(cart?.note || ""),
      attributes: copyProperties(cart?.attributes),
      items: Array.isArray(cart?.items) && cart.items.some((item) => item?._LineIdentity)
        ? cart.items
        : klaviyoItems(cart || {}),
    };
  }

  function clearLatestTrackedCart(recoveryId = "") {
    try {
      const latest = latestTrackedCart();
      if (!recoveryId || latest?.recoveryId === String(recoveryId)) {
        global.localStorage?.removeItem(LATEST_TRACKED_CART_KEY);
        validatedTrackedCartToken = "";
      }
    } catch (_error) {}
  }

  function ensureTrackedCartBaseline(cart) {
    const settings = config();
    if (
      restoring ||
      !settings.apiUrl ||
      !settings.auth ||
      !isCompleteCartSnapshot(cart) ||
      (validatedTrackedCartToken && latestTrackedCart(validatedTrackedCartToken))
    ) return Promise.resolve(cart || null);

    return withCrossTabLock(CAPTURE_STATE_LOCK, () => {
      const cartToken = String(cart?.token || "");
      if (latestTrackedCart(cartToken)) {
        validatedTrackedCartToken = cartToken;
        return cart;
      }
      const recovery = latestRecovery();
      const recoveryMatchesCart = recovery && (
        !recovery.cartToken ||
        !cart?.token ||
        recovery.cartToken === String(cart.token)
      );
      setLatestTrackedCart(
        cart,
        recoveryMatchesCart ? recovery.recoveryId : "",
        recoveryMatchesCart ? recovery.captureMarker : "",
      );
      return cart;
    });
  }

  async function responseJsonWithin(response, timeoutMs = POST_MUTATION_CART_TIMEOUT_MS) {
    let timeoutId;
    try {
      return await Promise.race([
        Promise.resolve()
          .then(() => response.clone().json())
          .then(
            (value) => ({ value, timedOut: false }),
            () => ({ value: null, timedOut: false }),
          ),
        new Promise((resolve) => {
          timeoutId = global.setTimeout(() => resolve({ value: null, timedOut: true }), timeoutMs);
        }),
      ]);
    } catch (_error) {
      return { value: null, timedOut: false };
    } finally {
      global.clearTimeout(timeoutId);
    }
  }

  async function responseCartToken(response) {
    const { value: cart } = await responseJsonWithin(response);
    return String(cart?.token || "");
  }

  function discountCodesFromMutation(input, payload) {
    if (!requestPath(input).endsWith("/cart/update")) return null;
    if (!payload || typeof payload !== "object" ||
      !Object.prototype.hasOwnProperty.call(payload, "discount")) return null;
    return normalizeDiscountCodes(payload.discount);
  }

  function assignMutationParameter(payload, key, value) {
    const updateMatch = String(key).match(/^updates\[([^\]]*)\]$/);
    if (updateMatch) {
      if (updateMatch[1] === "") {
        if (!Array.isArray(payload.updates)) payload.updates = [];
        payload.updates.push(value);
      } else {
        payload.updates ||= {};
        payload.updates[updateMatch[1]] = value;
      }
      return;
    }
    const attributeMatch = String(key).match(/^attributes\[([^\]]+)\]$/);
    if (attributeMatch) {
      payload.attributes ||= {};
      payload.attributes[attributeMatch[1]] = value;
      return;
    }
    const propertyMatch = String(key).match(/^properties\[([^\]]+)\]$/);
    if (propertyMatch) {
      payload.properties ||= {};
      payload.properties[propertyMatch[1]] = value;
      return;
    }
    const itemMatch = String(key).match(/^items\[(\d+)\]\[([^\]]+)\](?:\[([^\]]+)\])?$/);
    if (itemMatch) {
      payload.items ||= [];
      const item = payload.items[Number(itemMatch[1])] ||= {};
      if (itemMatch[3]) {
        item[itemMatch[2]] ||= {};
        item[itemMatch[2]][itemMatch[3]] = value;
      } else {
        item[itemMatch[2]] = value;
      }
      return;
    }
    payload[key] = value;
  }

  function mutationPayloadFromEntries(entries) {
    const payload = {};
    for (const [key, value] of entries) assignMutationParameter(payload, key, value);
    return payload;
  }

  async function requestBodyWithin(input, timeoutMs = POST_MUTATION_CART_TIMEOUT_MS) {
    let timeoutId;
    try {
      const request = input.clone();
      const contentType = String(request.headers?.get?.("content-type") || "").toLowerCase();
      return await Promise.race([
        Promise.resolve().then(() => contentType.includes("multipart/form-data")
          ? request.formData()
          : request.text()),
        new Promise((resolve) => {
          timeoutId = global.setTimeout(() => resolve(null), timeoutMs);
        }),
      ]);
    } catch (_error) {
      return null;
    } finally {
      global.clearTimeout(timeoutId);
    }
  }

  async function cartMutationPayload(input, options = {}) {
    let body = options.body;
    if (body === undefined && typeof input?.clone === "function") {
      body = await requestBodyWithin(input);
    }

    if (typeof body === "string") {
      try {
        return JSON.parse(body);
      } catch (_error) {
        return mutationPayloadFromEntries(new global.URLSearchParams(body).entries());
      }
    }
    if (body && typeof body.entries === "function") {
      return mutationPayloadFromEntries(body.entries());
    }
    if (body && typeof body === "object") return body;
    return null;
  }

  function requestedLineIdentity(item) {
    const variantId = positiveInteger(item?.id || item?.variant_id);
    if (!variantId) return "";
    return JSON.stringify({
      variantId: String(variantId),
      sellingPlanId: String(positiveInteger(item?.selling_plan || item?.selling_plan_id) || ""),
      properties: comparableProperties(item?.properties),
    });
  }

  function cartItemIdentity(item) {
    const variantId = positiveInteger(item?.variant_id || item?.id);
    if (!variantId) return "";
    return JSON.stringify({
      variantId: String(variantId),
      sellingPlanId: String(
        item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id ||
        "",
      ),
      properties: comparableProperties(item?.properties),
    });
  }

  function stateLineByReference(state, reference) {
    const normalized = String(reference || "");
    const byKey = state?.items?.filter((item) => String(item.LineItemKey || "") === normalized) || [];
    if (byKey.length === 1) return byKey[0];
    const byVariant = state?.items?.filter((item) => String(item.VariantID || "") === normalized) || [];
    return byVariant[0] || null;
  }

  function stateLineQuantity(state, identity) {
    return (state?.items || []).reduce((quantity, item) =>
      quantity + (String(item._LineIdentity || "") === identity ? Number(item.Quantity) || 0 : 0), 0);
  }

  function quantityUpdateApplied(
    reference,
    quantity,
    previousState,
    currentState,
    payload = {},
    lineNumberReference = false,
  ) {
    const previousItem = lineNumberReference
      ? previousState?.items?.[(positiveInteger(reference) || 0) - 1]
      : stateLineByReference(previousState, reference);
    if (!previousItem?.LineItemKey) {
      if (lineNumberReference) return false;
      const variantId = positiveInteger(reference);
      const normalizedQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
      if (!variantId || !normalizedQuantity) return false;
      const previousVariantItems = (previousState?.items || []).filter((item) =>
        String(item.VariantID || "") === String(variantId),
      );
      const currentVariantItems = (currentState?.items || []).filter((item) =>
        String(item.VariantID || "") === String(variantId),
      );
      return !previousVariantItems.length && currentVariantItems.length === 1 &&
        Boolean(currentVariantItems[0].LineItemKey) &&
        Number(currentVariantItems[0].Quantity) === normalizedQuantity;
    }
    if (Object.prototype.hasOwnProperty.call(payload || {}, "properties") ||
      Object.prototype.hasOwnProperty.call(payload || {}, "selling_plan")) return false;
    const normalizedQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
    let currentItem = (currentState?.items || []).find((item) =>
      String(item.LineItemKey || "") === String(previousItem.LineItemKey),
    );
    if (!currentItem && normalizedQuantity > 0 && previousItem._LineIdentity) {
      const identityMatches = (currentState?.items || []).filter((item) =>
        String(item._LineIdentity || "") === String(previousItem._LineIdentity),
      );
      if (identityMatches.length === 1) [currentItem] = identityMatches;
    }
    return normalizedQuantity === 0
      ? !currentItem
      : Number(currentItem?.Quantity) === normalizedQuantity;
  }

  function cartMutationApplied(path, payload, previousCart, currentCart) {
    if (!payload || !currentCart || !previousCart) return false;
    const previousState = mutationCartState(previousCart);
    const currentState = mutationCartState(currentCart);
    if (!previousState.cartToken || previousState.cartToken !== currentState.cartToken) return false;

    if (path.endsWith("/cart/add")) {
      const items = Array.isArray(payload.items) ? payload.items : [payload];
      const requestedQuantities = new Map();
      for (const item of items) {
        const identity = requestedLineIdentity(item);
        const quantity = positiveInteger(item?.quantity || 1);
        if (!identity || !quantity) return false;
        requestedQuantities.set(identity, (requestedQuantities.get(identity) || 0) + quantity);
      }
      return [...requestedQuantities].every(([identity, quantity]) =>
        stateLineQuantity(currentState, identity) >=
          stateLineQuantity(previousState, identity) + quantity,
      );
    }

    if (path.endsWith("/cart/change")) {
      const reference = payload.line ?? payload.id;
      return reference !== undefined && Object.prototype.hasOwnProperty.call(payload, "quantity") &&
        quantityUpdateApplied(
          reference,
          payload.quantity,
          previousState,
          currentState,
          payload,
          payload.line !== undefined,
        );
    }

    if (path.endsWith("/cart/update")) {
      let verified = false;
      if (payload.updates && typeof payload.updates === "object") {
        const positionalUpdates = Array.isArray(payload.updates);
        const updates = Object.entries(payload.updates);
        if (!updates.length || !updates.every(([reference, quantity]) =>
          quantityUpdateApplied(
            positionalUpdates ? Number(reference) + 1 : reference,
            quantity,
            previousState,
            currentState,
            {},
            positionalUpdates,
          ))) return false;
        verified = true;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "note")) {
        if (String(currentState.note) !== String(payload.note || "")) return false;
        verified = true;
      }
      if (payload.attributes && typeof payload.attributes === "object") {
        if (!Object.entries(payload.attributes).every(([key, value]) =>
          String(currentState.attributes[key] || "") === String(value || ""))) return false;
        verified = true;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "discount")) {
        const requestedCodes = normalizeDiscountCodes(payload.discount);
        const observedCodes = requestedCodes.length
          ? currentState.discountCodes
          : cartDiscountCodes(currentCart, false);
        const currentCodes = new Set(observedCodes.map((code) => code.toLowerCase()));
        if (!requestedCodes.every((code) => currentCodes.has(code.toLowerCase()))) return false;
        if (!requestedCodes.length && observedCodes.length) return false;
        verified = true;
      }
      return verified;
    }

    return path.endsWith("/cart/clear") && currentState.itemCount === 0;
  }

  function lineReference(item, index) {
    return String(item?.key || `line-${index + 1}`);
  }

  function promotionParents(items, referencesByItem) {
    return items.reduce((parents, item) => {
      const properties = item?.properties || {};
      const token = properties._tpp_promotion_parent_token;
      if (token && properties._tpp_promotion_type_ids) {
        parents.set(String(token), {
          lineReference: referencesByItem.get(item),
          variantId: positiveInteger(item.variant_id || item.id),
        });
      }
      return parents;
    }, new Map());
  }

  function addOnParents(items, referencesByItem) {
    return items.reduce((parents, item) => {
      const token = item?.properties?._tpp_add_on_parent_token;
      if (token && !item?.properties?._tpp_add_on_for) {
        parents.set(String(token), {
          lineReference: referencesByItem.get(item),
          variantId: positiveInteger(item.variant_id || item.id),
        });
      }
      return parents;
    }, new Map());
  }

  function resolveParent(item, items, itemsByKey, referencesByItem, parentsByToken, addOnParentsByToken) {
    const properties = item?.properties || {};
    const promotionParent = properties._tpp_promotion_type_id && properties._tpp_promotion_parent_token
      ? parentsByToken.get(String(properties._tpp_promotion_parent_token)) || {}
      : {};
    const addOnTokenParent = properties._tpp_add_on_for && properties._tpp_add_on_parent_token
      ? addOnParentsByToken.get(String(properties._tpp_add_on_parent_token)) || {}
      : {};
    const relationship = item?.parent_relationship?.parent || item?.parent_relationship;
    const relationshipVariant = positiveInteger(
      relationship?.variant_id || relationship?.id || relationship?.parent_id,
    );
    const relationshipKey = String(relationship?.parent_key || relationship?.key || "");
    const relationshipParent = relationshipKey ? itemsByKey.get(relationshipKey) : null;
    const keyedVariant = positiveInteger(relationshipParent?.variant_id || relationshipParent?.id);
    if (relationshipVariant || keyedVariant || relationshipKey) {
      return {
        lineReference: referencesByItem.get(relationshipParent) || promotionParent.lineReference || addOnTokenParent.lineReference || relationshipKey || null,
        variantId: relationshipVariant || keyedVariant || promotionParent.variantId || addOnTokenParent.variantId || null,
      };
    }

    const addOnParentVariant = positiveInteger(properties._tpp_add_on_for);
    if (addOnParentVariant) {
      const candidates = items.filter((candidate) =>
        candidate !== item && positiveInteger(candidate?.variant_id || candidate?.id) === addOnParentVariant,
      );
      return {
        lineReference: addOnTokenParent.lineReference || (candidates.length === 1 ? referencesByItem.get(candidates[0]) : null),
        variantId: addOnParentVariant,
      };
    }

    return promotionParent;
  }

  function buildSnapshot(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const itemsByKey = new Map(items.map((item) => [String(item?.key || ""), item]));
    const referencesByItem = new Map(items.map((item, index) => [item, lineReference(item, index)]));
    const parentsByToken = promotionParents(items, referencesByItem);
    const addOnParentsByToken = addOnParents(items, referencesByItem);

    return {
      currency: cart?.currency || null,
      discount_codes: cartDiscountCodes(cart),
      note: cart?.note || null,
      attributes: copyProperties(cart?.attributes),
      total_price: Number.parseInt(cart?.total_price, 10) || 0,
      items: items.map((item) => {
        const sellingPlanId = positiveInteger(
          item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan_allocation?.selling_plan_id,
        );
        const parent = resolveParent(item, items, itemsByKey, referencesByItem, parentsByToken, addOnParentsByToken);
        return {
          variant_id: positiveInteger(item?.variant_id || item?.id),
          quantity: positiveInteger(item?.quantity),
          properties: copyProperties(item?.properties),
          line_reference: referencesByItem.get(item),
          ...(sellingPlanId ? { selling_plan_id: sellingPlanId } : {}),
          ...(parent.variantId ? { parent_variant_id: parent.variantId } : {}),
          ...(parent.lineReference ? { parent_line_reference: parent.lineReference } : {}),
        };
      }).filter((item) => item.variant_id && item.quantity),
    };
  }

  function restorePlan(items) {
    return (Array.isArray(items) ? items : []).map((item, index) => ({
      id: positiveInteger(item.variant_id),
      quantity: positiveInteger(item.quantity),
      properties: copyProperties(item.properties),
      lineReference: String(item.line_reference || `line-${index + 1}`),
      parentLineReference: String(item.parent_line_reference || ""),
      parentVariantId: positiveInteger(item.parent_variant_id),
      sellingPlanId: positiveInteger(item.selling_plan_id),
    })).filter((item) => item.id && item.quantity);
  }

  function restoreItemPayload(item, parentLineKey = "") {
    return {
      id: item.id,
      quantity: item.quantity,
      properties: item.properties,
      ...(item.sellingPlanId ? { selling_plan: item.sellingPlanId } : {}),
      ...(parentLineKey ? { parent_line_key: parentLineKey } : {}),
    };
  }

  function buildRestoreItems(items) {
    return restorePlan(items)
      .sort((left, right) => Number(Boolean(left.parentLineReference || left.parentVariantId)) -
        Number(Boolean(right.parentLineReference || right.parentVariantId)))
      .map((item) => restoreItemPayload(item));
  }

  function recoveryUrl(id) {
    const url = new URL(route(RECOVERY_LANDING_PATH), global.location.origin);
    url.searchParams.set(RECOVERY_PARAMETER, id);
    return url.toString();
  }

  function klaviyoItems(cart) {
    return (cart.items || []).map((item, index) => ({
      _LineIdentity: JSON.stringify({
        variantId: String(item.variant_id || item.id || ""),
        sellingPlanId: String(
          item?.selling_plan_allocation?.selling_plan?.id ||
          item?.selling_plan_allocation?.selling_plan_id ||
          "",
        ),
        properties: comparableProperties(item.properties),
      }),
      LineItemKey: String(item.key || `line-${index + 1}`),
      ProductID: String(item.product_id || ""),
      VariantID: String(item.variant_id || ""),
      ProductName: item.product_title || item.title || "",
      VariantName: item.variant_title || "",
      Quantity: item.quantity || 0,
      Price: (Number(item.final_price ?? item.price) || 0) / 100,
      ProductURL: item.url ? new URL(item.url, global.location.origin).toString() : "",
      ImageURL: item.image || "",
      SKU: item.sku || "",
      LineValue: (Number(item.final_line_price ??
        (Number(item.final_price ?? item.price) || 0) * (Number(item.quantity) || 0)) || 0) / 100,
    }));
  }

  function klaviyoLineChanged(currentItem, previousItem, ignorePriceChanges = false) {
    const properties = [
      "ProductID",
      "VariantID",
      "ProductName",
      "VariantName",
      "Quantity",
      "SKU",
    ];
    if (!ignorePriceChanges) properties.push("Price", "LineValue");
    return properties.some((property) => currentItem[property] !== previousItem[property]);
  }

  function comparableProperties(properties) {
    return Object.fromEntries(
      Object.entries(copyProperties(properties)).sort(([left], [right]) => left.localeCompare(right)),
    );
  }

  function cartMetadataChanges(cart, previousCart) {
    if (!previousCart) return [];

    const changes = [];
    if (String(cart?.note || "") !== String(previousCart.note || "")) {
      changes.push("note");
    }
    if (
      JSON.stringify(comparableProperties(cart?.attributes)) !==
      JSON.stringify(comparableProperties(previousCart.attributes))
    ) {
      changes.push("attributes");
    }
    const currentDiscounts = cartDiscountCodes(cart).slice().sort();
    const previousDiscounts = normalizeDiscountCodes(previousCart.discountCodes).slice().sort();
    if (JSON.stringify(currentDiscounts) !== JSON.stringify(previousDiscounts)) {
      changes.push("discount_codes");
    }
    return changes;
  }

  function fallbackLineIdentity(item) {
    return JSON.stringify([
      String(item.ProductID || ""),
      String(item.VariantID || ""),
      String(item.ProductName || ""),
      String(item.VariantName || ""),
      String(item.SKU || ""),
    ]);
  }

  function matchCartLines(currentItems, previousItems) {
    const matches = new Map();
    const unmatchedPrevious = new Set(previousItems.map((_item, index) => index));

    function matchBy(currentIdentity, previousIdentity = currentIdentity) {
      const previousByIdentity = new Map();
      unmatchedPrevious.forEach((index) => {
        const value = previousIdentity(previousItems[index]);
        if (!value) return;
        const candidates = previousByIdentity.get(value) || [];
        candidates.push(index);
        previousByIdentity.set(value, candidates);
      });

      currentItems.forEach((item, currentIndex) => {
        if (matches.has(currentIndex)) return;
        const value = currentIdentity(item);
        const candidates = value ? previousByIdentity.get(value) : null;
        const previousIndex = candidates?.shift();
        if (previousIndex === undefined) return;
        matches.set(currentIndex, previousIndex);
        unmatchedPrevious.delete(previousIndex);
      });
    }

    matchBy((item) => String(item.LineItemKey || ""));
    matchBy((item) => String(item._LineIdentity || ""));
    matchBy(
      fallbackLineIdentity,
      (item) => item._LineIdentity ? "" : fallbackLineIdentity(item),
    );

    return { matches, unmatchedPrevious };
  }

  function klaviyoCartUpdate(cart, previousCart = null) {
    const currentItems = klaviyoItems(cart);
    const previousItems = previousCart?.items || [];
    const cartChanges = cartMetadataChanges(cart, previousCart);
    const ignorePriceChanges = cartChanges.includes("discount_codes");
    const { matches, unmatchedPrevious } = matchCartLines(currentItems, previousItems);
    const changedItems = [];

    currentItems.forEach((item, currentIndex) => {
      const previousIndex = matches.get(currentIndex);
      const previousItem = previousIndex === undefined ? null : previousItems[previousIndex];
      if (!previousItem || klaviyoLineChanged(item, previousItem, ignorePriceChanges)) {
        const previousQuantity = Number(previousItem?.Quantity) || 0;
        const quantityDelta = item.Quantity - previousQuantity;
        changedItems.push({
          ...item,
          ChangeType: !previousItem
            ? "added"
            : quantityDelta > 0
              ? "quantity_increased"
              : quantityDelta < 0
                ? "quantity_decreased"
                : "updated",
          PreviousQuantity: previousQuantity,
          QuantityDelta: quantityDelta,
          PreviousLineValue: Number(previousItem?.LineValue) || 0,
        });
      }
    });

    previousItems.forEach((item, index) => {
      if (!unmatchedPrevious.has(index)) return;
      changedItems.push({
        ...item,
        ChangeType: "removed",
        PreviousQuantity: Number(item.Quantity) || 0,
        Quantity: 0,
        QuantityDelta: -(Number(item.Quantity) || 0),
        PreviousLineValue: Number(item.LineValue) || 0,
        LineValue: 0,
      });
    });

    return {
      changedItems,
      cartChanges,
      previousItemCount: Number(previousCart?.itemCount) || 0,
      previousTotalPrice: Number(previousCart?.totalPrice) || 0,
    };
  }

  function klaviyoLineEventId(captureMarker, item, eventIdSuffix = "") {
    return [captureMarker, item.LineItemKey, eventIdSuffix].filter(Boolean).join(":").slice(0, 255);
  }

  function eventLineItem(item) {
    const { _LineIdentity: _lineIdentity, ...properties } = item;
    return properties;
  }

  function recoveryEvents(cart, recovery, update, captureMarker, eventIdSuffix = "") {
    const settings = config();
    if (settings.suppressEvents) return [];

    const cartValue = (Number(cart.total_price) || 0) / 100;
    const cartProperties = {
      RecoveryURL: recoveryUrl(recovery.id),
      RecoveryID: recovery.id,
      ExpiresAt: recovery.expires_at,
      CartItemCount: cart.item_count || 0,
      PreviousCartItemCount: update.previousItemCount,
      CartItemCountDelta: (Number(cart.item_count) || 0) - update.previousItemCount,
      CartValue: cartValue,
      PreviousCartValue: update.previousTotalPrice / 100,
      CartValueDelta: ((Number(cart.total_price) || 0) - update.previousTotalPrice) / 100,
      CartChanges: update.cartChanges,
      IsThemePreview: settings.isPreview,
      $currency: cart.currency || "USD",
    };
    const eventItems = update.changedItems.length ? update.changedItems : [{
      LineItemKey: "cart",
      ChangeType: "cart_updated",
    }];
    return eventItems.map((rawItem) => {
      const item = eventLineItem(rawItem);
      return {
        ...cartProperties,
        ...item,
        Items: update.changedItems.length ? [item] : [],
        $value: cartValue,
        $event_id: klaviyoLineEventId(captureMarker, item, eventIdSuffix),
      };
    });
  }

  function durableRecoveryHandoff(
    cart,
    recovery,
    update,
    captureMarker,
    eventIdSuffix = "",
  ) {
    const profile = durableRecoveryProfile();
    const exchangeId = String(profile.exchangeId || "").trim();
    const anonymousId = String(profile.anonymousId || "").trim();
    const events = recoveryEvents(
      cart,
      recovery,
      update,
      captureMarker,
      eventIdSuffix,
    );
    if ((!exchangeId && !anonymousId) || !events.length) return null;
    return {
      ...(exchangeId ? { exchange_id: exchangeId } : {}),
      ...(anonymousId ? { anonymous_id: anonymousId } : {}),
      events: events.map((properties) => ({
        unique_id: properties.$event_id,
        value: properties.$value,
        value_currency: properties.$currency,
        properties,
      })),
    };
  }

  function durableRecoveryProfile() {
    return global.TPPKlaviyoIdentityGuard?.profileIdentifier?.() || {};
  }

  async function trackRecovery(cart, recovery, update, captureMarker, eventIdSuffix = "") {
    const events = recoveryEvents(cart, recovery, update, captureMarker, eventIdSuffix);
    if (!events.length) return;

    if (typeof global.klaviyo?.track === "function") {
      let timeoutId;
      try {
        const results = await Promise.race([
          Promise.all(events.map((properties) =>
            Promise.resolve(global.klaviyo.track(RECOVERY_EVENT, properties)),
          )),
          new Promise((_, reject) => {
            timeoutId = global.setTimeout(() => {
              reject(new Error("Klaviyo recovery tracking timed out."));
            }, KLAVIYO_TRACK_TIMEOUT_MS);
          }),
        ]);
        if (results.some((result) => result === false || result === 0)) {
          throw new Error("Klaviyo rejected a recovery tracking event.");
        }
      } finally {
        global.clearTimeout(timeoutId);
      }
    } else {
      global._klOnsite = global._klOnsite || [];
      events.forEach((properties) => {
        global._klOnsite.push(["track", RECOVERY_EVENT, properties]);
      });
    }
  }

  function config() {
    const value = global.pawPrintConfig || {};
    return {
      apiUrl: String(value.cartRecoveryUrl || "").replace(/\/$/, ""),
      auth: String(value.cartRecoveryAuth || value.latestOfferAssemblyAuth || ""),
      isPreview: value.cartRecoveryIsPreview === true,
      suppressEvents: value.cartRecoverySuppressEvents === true,
    };
  }

  function markCapturePending() {
    try {
      global.sessionStorage?.setItem(PENDING_CAPTURE_KEY, String(Date.now()));
    } catch (_error) {}
  }

  function pendingCartSnapshot() {
    try {
      const cart = JSON.parse(global.sessionStorage?.getItem(PENDING_CART_KEY) || "null");
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (_error) {
      return null;
    }
  }

  function currentCartSnapshot() {
    try {
      const cart = JSON.parse(global.sessionStorage?.getItem(CURRENT_CART_KEY) || "null");
      return isCompleteCartSnapshot(cart) ? cart : null;
    } catch (_error) {
      return null;
    }
  }

  function storeCurrentCartSnapshot(cart) {
    try {
      if (isCompleteCartSnapshot(cart)) {
        global.sessionStorage?.setItem(CURRENT_CART_KEY, JSON.stringify(cart));
      }
    } catch (_error) {}
  }

  function pendingCartMarker() {
    try {
      return String(global.sessionStorage?.getItem(PENDING_CART_MARKER_KEY) || "");
    } catch (_error) {
      return "";
    }
  }

  function latestRestoreMarker() {
    try {
      return String(global.localStorage?.getItem(LATEST_RESTORE_KEY) || "");
    } catch (_error) {
      return "";
    }
  }

  function pendingCartRestoreMarker() {
    try {
      return String(global.sessionStorage?.getItem(PENDING_CART_RESTORE_MARKER_KEY) || "");
    } catch (_error) {
      return "";
    }
  }

  function storePendingCartSnapshot(cart, captureMarker = "") {
    try {
      if (!cart || !Array.isArray(cart.items)) {
        global.sessionStorage?.removeItem(PENDING_CART_KEY);
        global.sessionStorage?.removeItem(PENDING_CART_MARKER_KEY);
        global.sessionStorage?.removeItem(PENDING_CART_RESTORE_MARKER_KEY);
        return;
      }
      global.sessionStorage?.setItem(PENDING_CART_KEY, JSON.stringify({
        token: cart.token,
        currency: cart.currency,
        discount_codes: cart.discount_codes,
        discount_applications: cart.discount_applications,
        cart_level_discount_applications: cart.cart_level_discount_applications,
        note: cart.note,
        attributes: cart.attributes,
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: cart.items,
      }));
      global.sessionStorage?.setItem(
        PENDING_CART_RESTORE_MARKER_KEY,
        latestRestoreMarker(),
      );
      if (captureMarker) {
        global.sessionStorage?.setItem(PENDING_CART_MARKER_KEY, captureMarker);
      } else {
        global.sessionStorage?.removeItem(PENDING_CART_MARKER_KEY);
      }
    } catch (_error) {}
  }

  function isCompleteCartSnapshot(cart) {
    return Boolean(
      cart &&
      Array.isArray(cart.items) &&
      String(cart.token || "") &&
      Number.isFinite(Number(cart.item_count)) &&
      Number.isFinite(Number(cart.total_price)),
    );
  }

  async function cartSnapshotFromResponse(response) {
    const { value: cart, timedOut } = await responseJsonWithin(response);
    return {
      cart: isCompleteCartSnapshot(cart) ? cart : null,
      timedOut,
    };
  }

  async function readCurrentCartSnapshot() {
    let timeoutId;
    const controller = typeof global.AbortController === "function" ?
      new global.AbortController() : null;
    try {
      return await Promise.race([
        jsonRequest(route("cart.js"), {
          headers: { Accept: "application/json" },
          ...(controller ? { signal: controller.signal } : {}),
        }).then((cart) => isCompleteCartSnapshot(cart) ? cart : null),
        new Promise((resolve) => {
          timeoutId = global.setTimeout(() => {
            controller?.abort();
            resolve(null);
          }, POST_MUTATION_CART_TIMEOUT_MS);
        }),
      ]);
    } catch (_error) {
      return null;
    } finally {
      global.clearTimeout(timeoutId);
    }
  }

  function currentLatestCaptureMarker() {
    let marker = latestCaptureMarker;
    try {
      marker = global.localStorage?.getItem(LATEST_CAPTURE_KEY) || marker;
    } catch (_error) {}
    return marker;
  }

  function reserveLatestCaptureMarker(previousMarker = currentLatestCaptureMarker()) {
    const order = Math.max(Date.now() * 1000, markerOrder(previousMarker) + 1);
    const marker = `${order}-${CAPTURE_INSTANCE_ID}-${++captureMarkerCounter}`;
    latestCaptureMarker = marker;
    try {
      global.localStorage?.setItem(LATEST_CAPTURE_KEY, marker);
    } catch (_error) {}
    return marker;
  }

  function markLatestCapture(expectedGeneration = null) {
    return withCrossTabLock(CAPTURE_STATE_LOCK, () => {
      if (expectedGeneration !== null && expectedGeneration !== captureGeneration) return "";
      return reserveLatestCaptureMarker();
    });
  }

  function promotePendingCapture(expectedGeneration) {
    return withCrossTabLock(CAPTURE_STATE_LOCK, () => {
      if (expectedGeneration !== captureGeneration) return { marker: "", preparedCart: null };
      const previousMarker = currentLatestCaptureMarker();
      const preparedCart = pendingCartMarker() === previousMarker
        ? pendingCartSnapshot()
        : null;
      const marker = reserveLatestCaptureMarker(previousMarker);
      return { marker, preparedCart };
    });
  }

  function pendingCartForUnload(previousMarker = currentLatestCaptureMarker()) {
    const storedMarker = pendingCartMarker();
    return (
      (!storedMarker || storedMarker === previousMarker) &&
      pendingCartRestoreMarker() === latestRestoreMarker()
    ) ? pendingCartSnapshot() : null;
  }

  function promotePendingCaptureForUnload(expectedGeneration) {
    return withCrossTabLock(CAPTURE_STATE_LOCK, () => {
      if (expectedGeneration !== captureGeneration) {
        return { marker: "", preparedCart: null, preparedRestoreMarker: "" };
      }
      const previousMarker = currentLatestCaptureMarker();
      const preparedRestoreMarker = pendingCartRestoreMarker();
      const preparedCart = pendingCartForUnload(previousMarker);
      const marker = reserveLatestCaptureMarker(previousMarker);
      if (preparedCart) storePendingCartSnapshot(preparedCart, marker);
      return { marker, preparedCart, preparedRestoreMarker };
    });
  }

  function isLatestCapture(marker) {
    try {
      return (global.localStorage?.getItem(LATEST_CAPTURE_KEY) || latestCaptureMarker) === marker;
    } catch (_error) {
      return latestCaptureMarker === marker;
    }
  }

  function clearCapturePending() {
    try {
      global.sessionStorage?.removeItem(PENDING_CAPTURE_KEY);
      clearPendingCartSnapshot();
    } catch (_error) {}
  }

  function clearPendingCartSnapshot() {
    try {
      global.sessionStorage?.removeItem(PENDING_CART_KEY);
      global.sessionStorage?.removeItem(PENDING_CART_MARKER_KEY);
      global.sessionStorage?.removeItem(PENDING_CART_RESTORE_MARKER_KEY);
    } catch (_error) {}
  }

  function pendingCaptureExpired() {
    try {
      const createdAt = Number(global.sessionStorage?.getItem(PENDING_CAPTURE_KEY));
      return Boolean(createdAt && Date.now() - createdAt > PENDING_CART_MAX_AGE_MS);
    } catch (_error) {
      return false;
    }
  }

  function hasPendingCapture(preserveExpiredSnapshot = false) {
    try {
      const createdAt = Number(global.sessionStorage?.getItem(PENDING_CAPTURE_KEY));
      if (createdAt) {
        if (!preserveExpiredSnapshot && pendingCaptureExpired()) {
          clearPendingCartSnapshot();
        }
        return true;
      }
      clearCapturePending();
    } catch (_error) {}
    return false;
  }

  async function jsonRequest(url, options = {}) {
    const response = await nativeFetch(url, options);
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const error = new Error(payload.error || `Cart recovery request failed (${response.status}).`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  async function timedJsonRequest(url, options, timeoutMs, timeoutMessage) {
    const controller = typeof global.AbortController === "function"
      ? new global.AbortController()
      : null;
    let timedOut = false;
    let timeoutId;
    const deadline = new Promise((_, reject) => {
      timeoutId = global.setTimeout(() => {
        timedOut = true;
        controller?.abort();
        reject(new Error(timeoutMessage));
      }, timeoutMs);
    });

    try {
      return await Promise.race([
        jsonRequest(url, {
          ...options,
          ...(controller ? { signal: controller.signal } : {}),
        }),
        deadline,
      ]);
    } catch (error) {
      if (timedOut) throw new Error(timeoutMessage);
      throw error;
    } finally {
      global.clearTimeout(timeoutId);
    }
  }

  async function timedNativeRequest(input, options = {}, timeoutMs, timeoutMessage) {
    const controller = typeof global.AbortController === "function"
      ? new global.AbortController()
      : null;
    const callerSignal = options?.signal || input?.signal;
    let timedOut = false;
    let timeoutId;
    const forwardAbort = () => controller?.abort(callerSignal?.reason);
    if (controller && callerSignal) {
      if (callerSignal.aborted) {
        forwardAbort();
      } else {
        callerSignal.addEventListener?.("abort", forwardAbort, { once: true });
      }
    }
    const deadline = new Promise((_, reject) => {
      timeoutId = global.setTimeout(() => {
        timedOut = true;
        controller?.abort();
        const error = new Error(timeoutMessage);
        error.name = "CartMutationTimeoutError";
        reject(error);
      }, timeoutMs);
    });

    try {
      return await Promise.race([
        nativeFetch(input, {
          ...options,
          ...(controller ? { signal: controller.signal } : {}),
        }),
        deadline,
      ]);
    } catch (error) {
      if (timedOut) {
        const timeoutError = new Error(timeoutMessage);
        timeoutError.name = "CartMutationTimeoutError";
        throw timeoutError;
      }
      throw error;
    } finally {
      global.clearTimeout(timeoutId);
      callerSignal?.removeEventListener?.("abort", forwardAbort);
    }
  }

  function requestedSectionNames(payload) {
    const sections = Array.isArray(payload?.sections)
      ? payload.sections
      : String(payload?.sections || "").split(",");
    return [...new Set(sections.map((section) => String(section).trim()).filter(Boolean))];
  }

  async function reconciledSections(payload) {
    const names = requestedSectionNames(payload);
    if (!names.length) return null;
    const fallbackUrl = new URL(global.location.href, global.location.origin);
    let sectionUrl;
    try {
      sectionUrl = new URL(String(payload?.sections_url || fallbackUrl.pathname), global.location.origin);
      if (sectionUrl.origin !== global.location.origin) sectionUrl = fallbackUrl;
    } catch (_error) {
      sectionUrl = fallbackUrl;
    }
    sectionUrl.searchParams.set("sections", names.join(","));
    try {
      const sections = await timedJsonRequest(
        `${sectionUrl.pathname}${sectionUrl.search}`,
        { headers: { Accept: "application/json" } },
        CHECKOUT_CAPTURE_TIMEOUT_MS,
        "Cart section rendering request timed out.",
      );
      if (sections && typeof sections === "object") return sections;
    } catch (_error) {}
    return Object.fromEntries(names.map((name) => [name, null]));
  }

  function reconciledAddedItems(payload, previousCart, currentCart) {
    const requestedItems = Array.isArray(payload?.items) ? payload.items : [payload];
    const previousQuantities = new Map((previousCart?.items || []).map((item) => [
      String(item?.key || ""),
      Number(item?.quantity) || 0,
    ]));
    const remainingDeltas = new Map((currentCart?.items || []).map((item) => [
      String(item?.key || ""),
      Math.max(0, (Number(item?.quantity) || 0) -
        (previousQuantities.get(String(item?.key || "")) || 0)),
    ]));

    return requestedItems.map((requestedItem) => {
      const identity = requestedLineIdentity(requestedItem);
      const candidates = (currentCart?.items || []).filter((item) =>
        cartItemIdentity(item) === identity &&
        (remainingDeltas.get(String(item?.key || "")) || 0) > 0,
      );
      const matched = candidates.sort((left, right) =>
        (remainingDeltas.get(String(right?.key || "")) || 0) -
        (remainingDeltas.get(String(left?.key || "")) || 0),
      )[0];
      if (!matched) return null;
      const key = String(matched.key || "");
      remainingDeltas.set(key, Math.max(
        0,
        (remainingDeltas.get(key) || 0) - positiveInteger(requestedItem?.quantity || 1),
      ));
      return matched;
    }).filter(Boolean);
  }

  async function reconciledCartResponse(path, payload, previousCart, currentCart) {
    const sections = await reconciledSections(payload);
    let responsePayload = currentCart;
    if (path.endsWith("/cart/add")) {
      const addedItems = reconciledAddedItems(payload, previousCart, currentCart);
      responsePayload = Array.isArray(payload?.items)
        ? { items: addedItems }
        : { ...(addedItems[0] || {}) };
    }
    if (sections) responsePayload = { ...responsePayload, sections };
    const body = JSON.stringify(responsePayload);
    if (typeof global.Response === "function") {
      return new global.Response(body, {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }
    const createResponse = () => ({
      ok: true,
      status: 200,
      statusText: "OK",
      clone: createResponse,
      json: async () => responsePayload,
      text: async () => body,
    });
    return createResponse();
  }

  function captureJsonRequest(url, options = {}) {
    return timedJsonRequest(
      url,
      options,
      CART_CAPTURE_REQUEST_TIMEOUT_MS,
      "Cart capture request timed out.",
    );
  }

  function restoreJsonRequest(url, options = {}) {
    return timedJsonRequest(
      url,
      options,
      CART_RESTORE_REQUEST_TIMEOUT_MS,
      "Cart recovery request timed out.",
    );
  }

  function bodyByteLength(body) {
    if (typeof global.TextEncoder === "function") {
      return new global.TextEncoder().encode(body).byteLength;
    }
    if (typeof global.Blob === "function") {
      return new global.Blob([body]).size;
    }
    return String(body).length * 3;
  }

  function createRecoveryId() {
    if (typeof global.crypto?.randomUUID === "function") {
      return global.crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (typeof global.crypto?.getRandomValues === "function") {
      global.crypto.getRandomValues(bytes);
    } else {
      throw new Error("Secure cart recovery ID generation is unavailable.");
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10).join(""),
    ].join("-");
  }

  function captureRequestBody(
    cart,
    snapshot = buildSnapshot(cart),
    recoveryId = "",
    klaviyoHandoff = null,
  ) {
    return JSON.stringify({
      shop_origin: global.location.origin,
      locale_root: global.Shopify?.routes?.root || "/",
      ...(recoveryId ? { recovery_id: recoveryId } : {}),
      ...(klaviyoHandoff ? { klaviyo: klaviyoHandoff } : {}),
      cart: snapshot,
    });
  }

  function bytesToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }
    return global.btoa(binary);
  }

  function compressedCaptureRequestBody(body) {
    if (
      typeof global.pako?.gzip !== "function" ||
      typeof global.btoa !== "function"
    ) return null;
    try {
      const bytes = global.pako.gzip(body);
      return JSON.stringify({
        encoding: "gzip-base64",
        payload: bytesToBase64(bytes),
      });
    } catch (_error) {
      return null;
    }
  }

  function prepareCaptureRequestBody(body, keepalive) {
    if (!keepalive || bodyByteLength(body) <= KEEPALIVE_BODY_MAX_BYTES) {
      return { body, keepalive: Boolean(keepalive) };
    }
    const compressedBody = compressedCaptureRequestBody(body);
    return {
      body: compressedBody || body,
      keepalive: Boolean(
        compressedBody &&
        bodyByteLength(compressedBody) <= KEEPALIVE_BODY_MAX_BYTES
      ),
    };
  }

  function startRecoveryCapture(settings, body, keepalive, requestLifecycle = null) {
    const preparedRequest = prepareCaptureRequestBody(body, keepalive);
    if (!preparedRequest.keepalive) requestLifecycle?.onUnboundedRequest?.();
    const request = captureJsonRequest(settings.apiUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        auth: settings.auth,
      },
      body: preparedRequest.body,
      ...(preparedRequest.keepalive ? { keepalive: true } : {}),
    });
    requestLifecycle?.onRequestStarted?.(preparedRequest.keepalive);
    return request;
  }

  function isOversizedCapture(cart) {
    return isCompleteCartSnapshot(cart) &&
      bodyByteLength(captureRequestBody(cart)) > KEEPALIVE_BODY_MAX_BYTES;
  }

  async function publishRecovery(
    cart,
    recovery,
    cartUpdate,
    generation,
    marker,
    alreadyTracked = false,
    eventIdSuffix = "",
  ) {
    const publication = await withCrossTabLock(CAPTURE_STATE_LOCK, async () => {
      if (generation !== captureGeneration || !isLatestCapture(marker)) return null;
      const previousRecovery = latestRecovery();
      if (
        previousRecovery?.captureMarker &&
        compareOrderedMarkers(previousRecovery.captureMarker, marker) > 0
      ) return null;
      if (!alreadyTracked) {
        try {
          await trackRecovery(cart, recovery, cartUpdate, marker, eventIdSuffix);
        } catch (trackingError) {
          return { trackingError };
        }
      }
      if (generation !== captureGeneration || !isLatestCapture(marker)) {
        return { superseded: true };
      }
      setLatestRecovery(recovery.id, cart.token, marker);
      setLatestTrackedCart(cart, recovery.id, marker);
      clearCapturePending();
      return true;
    });
    if (publication?.trackingError) throw publication.trackingError;
    if (!publication || publication?.superseded) return null;
    return recovery;
  }

  async function captureCart(
    scheduledGeneration = null,
    scheduledMarker = "",
    keepalive = false,
    preparedCart = null,
    requestLifecycle = null,
  ) {
    const generation = scheduledGeneration ?? ++captureGeneration;
    const marker = scheduledMarker || await markLatestCapture(generation);
    if (!marker) return null;
    const settings = config();
    if (!settings.apiUrl || !settings.auth) return null;

    return withCrossTabLock(
      CART_RESTORE_LOCK,
      () => captureCartWhileLocked(
        generation,
        marker,
        keepalive,
        preparedCart,
        settings,
        "",
        requestLifecycle,
      ),
      "shared",
    );
  }

  async function captureCartWhileLocked(
    generation,
    marker,
    keepalive,
    preparedCart,
    settings,
    eventIdSuffix = "",
    requestLifecycle = null,
  ) {
    if (generation !== captureGeneration || !isLatestCapture(marker)) return null;

    // Recovery IDs can outlive the browser's latest-cart pointer once Klaviyo has
    // received them. Leave server snapshots available until their configured expiry.

    const cart = isCompleteCartSnapshot(preparedCart) ? preparedCart :
      await captureJsonRequest(route("cart.js"), {
        headers: { Accept: "application/json" },
        ...(keepalive ? { keepalive: true } : {}),
      });
    const previousTrackedCart = latestTrackedCart(cart.token);
    const cartUpdate = klaviyoCartUpdate(cart, previousTrackedCart);
    if (!Array.isArray(cart.items) || !cart.items.length) {
      await withCrossTabLock(CAPTURE_STATE_LOCK, async () => {
        if (generation !== captureGeneration || !isLatestCapture(marker)) return null;
        const previous = latestRecovery();
        if (
          previous?.captureMarker &&
          compareOrderedMarkers(previous.captureMarker, marker) > 0
        ) return null;
        const recoveryId = previous?.recoveryId || previousTrackedCart?.recoveryId;
        if (recoveryId && cartUpdate.changedItems.length) {
          await trackRecovery(cart, {
            id: recoveryId,
            expires_at: null,
          }, cartUpdate, marker, [eventIdSuffix, "empty"].filter(Boolean).join("-"));
          setLatestTrackedCart(cart, recoveryId, marker);
        }
        if (generation !== captureGeneration || !isLatestCapture(marker)) return;
        clearLatestRecovery(recoveryId);
        clearLatestTrackedCart(recoveryId);
        clearCapturePending();
      });
      return null;
    }

    const existingRecovery = latestRecovery();
    if (
      previousTrackedCart &&
      !cartUpdate.changedItems.length &&
      !cartUpdate.cartChanges.length &&
      existingRecovery?.recoveryId === previousTrackedCart.recoveryId
    ) {
      await withCrossTabLock(CAPTURE_STATE_LOCK, () => {
        if (generation !== captureGeneration || !isLatestCapture(marker)) return;
        clearCapturePending();
      });
      return existingRecovery;
    }

    if (requestLifecycle?.shouldStartRequest && !requestLifecycle.shouldStartRequest()) {
      return null;
    }

    const durableProfile = keepalive ? durableRecoveryProfile() : {};
    const hasDurableProfile = Boolean(
      String(durableProfile.exchangeId || "").trim() ||
      String(durableProfile.anonymousId || "").trim()
    );
    const preallocatedRecovery = hasDurableProfile ? {
      id: createRecoveryId(),
      expires_at: new Date(Date.now() + RECOVERY_LIFETIME_MS).toISOString(),
    } : null;
    const klaviyoHandoff = preallocatedRecovery
      ? durableRecoveryHandoff(
        cart,
        preallocatedRecovery,
        cartUpdate,
        marker,
        eventIdSuffix,
      )
      : null;
    const body = captureRequestBody(
      cart,
      buildSnapshot(cart),
      preallocatedRecovery?.id,
      klaviyoHandoff,
    );
    const recovery = await startRecoveryCapture(
      settings,
      body,
      keepalive,
      requestLifecycle,
    );
    if (preallocatedRecovery && recovery?.id !== preallocatedRecovery.id) {
      throw new Error("Cart recovery server did not preserve the preallocated ID.");
    }
    return publishRecovery(
      cart,
      recovery,
      cartUpdate,
      generation,
      marker,
      Boolean(klaviyoHandoff),
      eventIdSuffix,
    );
  }

  async function capturePreparedCartForUnload(
    generation,
    marker,
    preparedCart,
    preparedRestoreMarker,
    settings,
    requestLifecycle = null,
  ) {
    const preparedSnapshot = buildSnapshot(preparedCart);
    const preparedPreviousTrackedCart = latestTrackedCart(preparedCart.token);
    return withCrossTabLock(CART_RESTORE_LOCK, async () => {
      if (generation !== captureGeneration || !isLatestCapture(marker)) return null;
      if (preparedRestoreMarker !== latestRestoreMarker()) {
        return captureCartWhileLocked(
          generation,
          marker,
          true,
          null,
          settings,
          "revalidated",
          requestLifecycle,
        );
      }
      if (JSON.stringify(preparedSnapshot) !== JSON.stringify(buildSnapshot(preparedCart))) {
        return captureCartWhileLocked(
          generation,
          marker,
          true,
          preparedCart,
          settings,
          "revalidated",
          requestLifecycle,
        );
      }
      if (
        JSON.stringify(preparedPreviousTrackedCart) !==
        JSON.stringify(latestTrackedCart(preparedCart.token))
      ) {
        return captureCartWhileLocked(
          generation,
          marker,
          true,
          preparedCart,
          settings,
          "revalidated",
          requestLifecycle,
        );
      }
      if (requestLifecycle?.shouldStartRequest && !requestLifecycle.shouldStartRequest()) {
        return null;
      }

      const recovery = {
        id: createRecoveryId(),
        expires_at: new Date(Date.now() + RECOVERY_LIFETIME_MS).toISOString(),
      };
      const preparedCartUpdate = klaviyoCartUpdate(
        preparedCart,
        preparedPreviousTrackedCart,
      );
      const klaviyoHandoff = durableRecoveryHandoff(
        preparedCart,
        recovery,
        preparedCartUpdate,
        marker,
      );
      const body = captureRequestBody(
        preparedCart,
        preparedSnapshot,
        recovery.id,
        klaviyoHandoff,
      );
      const savedRecovery = await startRecoveryCapture(
        settings,
        body,
        true,
        requestLifecycle,
      );
      if (savedRecovery?.id !== recovery.id) {
        throw new Error("Cart recovery server did not preserve the preallocated ID.");
      }
      recovery.expires_at = savedRecovery.expires_at || recovery.expires_at;
      if (!klaviyoHandoff) {
        await trackRecovery(preparedCart, recovery, preparedCartUpdate, marker);
      }
      return publishRecovery(
        preparedCart,
        recovery,
        preparedCartUpdate,
        generation,
        marker,
        true,
      );
    }, "shared");
  }

  function runScheduledCapture(generation, marker, retryIndex = 0) {
    const preparedCart = pendingCartMarker() === marker ? pendingCartSnapshot() : null;
    return captureCart(generation, marker, false, preparedCart).catch(() => {
      scheduleCaptureRetry(generation, marker, retryIndex);
      return null;
    });
  }

  function scheduleCaptureRetry(generation, marker, retryIndex = 0) {
    if (
      generation !== captureGeneration ||
      !isLatestCapture(marker) ||
      retryIndex >= CAPTURE_RETRY_DELAYS_MS.length
    ) return;
    captureTimer = global.setTimeout(() => {
      runScheduledCapture(generation, marker, retryIndex + 1);
    }, CAPTURE_RETRY_DELAYS_MS[retryIndex]);
  }

  function scheduleCapture(delay = CAPTURE_DELAY_MS, cart = null) {
    if (restoring) return;
    storeCurrentCartSnapshot(cart);
    const generation = ++captureGeneration;
    markCapturePending();
    storePendingCartSnapshot(cart);
    global.clearTimeout(captureTimer);
    return markLatestCapture(generation).then((marker) => {
      if (!marker) return;
      if (generation !== captureGeneration || !isLatestCapture(marker)) return;
      storePendingCartSnapshot(cart, marker);
      captureTimer = global.setTimeout(() => {
        runScheduledCapture(generation, marker);
      }, delay);
      return null;
    });
  }

  async function captureScheduledCartWhileLocked(cart) {
    if (restoring) return null;
    const settings = config();
    if (!settings.apiUrl || !settings.auth) return null;
    const generation = ++captureGeneration;
    markCapturePending();
    storePendingCartSnapshot(cart);
    global.clearTimeout(captureTimer);
    const marker = await markLatestCapture(generation);
    if (!marker) return null;
    if (generation !== captureGeneration || !isLatestCapture(marker)) return null;
    storePendingCartSnapshot(cart, marker);
    try {
      return await captureCartWhileLocked(
        generation,
        marker,
        false,
        cart,
        settings,
      );
    } catch (_error) {
      scheduleCaptureRetry(generation, marker);
      return null;
    }
  }

  function captureScheduledCart(cart) {
    return withCrossTabLock(
      CART_RESTORE_LOCK,
      () => captureScheduledCartWhileLocked(cart),
      "shared",
    ).catch(() => null);
  }

  async function flushPendingCapture(
    keepalive = false,
    preserveExpiredSnapshot = false,
    onUnboundedRequest = null,
    onRequestStarted = null,
    shouldStartRequest = null,
  ) {
    if (!hasPendingCapture(preserveExpiredSnapshot)) return null;
    const generation = ++captureGeneration;
    global.clearTimeout(captureTimer);
    const { marker, preparedCart, preparedRestoreMarker } = keepalive
      ? await promotePendingCaptureForUnload(generation)
      : await promotePendingCapture(generation);
    if (!marker) return null;
    if (generation !== captureGeneration || !isLatestCapture(marker)) return null;
    try {
      if (
        keepalive &&
        isCompleteCartSnapshot(preparedCart) &&
        preparedCart.items.length
      ) {
        const settings = config();
        if (!settings.apiUrl || !settings.auth) return null;
        // Accelerated wallets may unload the page as soon as their event resumes.
        // Revalidate under the restore lock, then signal the handler as soon as
        // the durable request has actually started.
        return await capturePreparedCartForUnload(
          generation,
          marker,
          preparedCart,
          preparedRestoreMarker,
          settings,
          { onUnboundedRequest, onRequestStarted, shouldStartRequest },
        );
      }
      return await captureCart(
        generation,
        marker,
        keepalive,
        preparedCart,
        { onUnboundedRequest, onRequestStarted, shouldStartRequest },
      );
    } catch (error) {
      scheduleCaptureRetry(generation, marker);
      throw error;
    }
  }

  async function flushPendingCaptureBeforeCheckout(
    keepalive = false,
    preserveExpiredSnapshot = false,
    onUnboundedRequest = null,
    requireCompletion = false,
    onRequestStarted = null,
    shouldStartRequest = null,
  ) {
    // A synchronous/oversized mutation may have completed its capture before
    // the checkout caller gets here. No pending work is checkout-ready, not a
    // failed flush.
    if (!hasPendingCapture(preserveExpiredSnapshot)) return true;
    let timeoutId;
    let completed = false;
    try {
      let notifyRequestStarted;
      const requestStarted = new Promise((resolve) => {
        notifyRequestStarted = resolve;
      });
      const capture = flushPendingCapture(
        keepalive,
        preserveExpiredSnapshot,
        () => {
          onUnboundedRequest?.();
        },
        (keepaliveStarted) => {
          notifyRequestStarted(Boolean(keepaliveStarted));
          onRequestStarted?.(keepaliveStarted);
        },
        shouldStartRequest,
      ).then(() => {
        completed = !hasPendingCapture(preserveExpiredSnapshot);
      });
      if (requireCompletion) {
        await capture;
        return completed;
      }
      const firstResult = await Promise.race([
        capture.then(() => ({ completed: true })),
        requestStarted.then((keepaliveStarted) => ({ keepaliveStarted })),
      ]);
      if (firstResult.completed) return completed;
      if (!firstResult.keepaliveStarted) {
        await capture;
        return completed;
      }
      await Promise.race([
        capture,
        new Promise((resolve) => {
          timeoutId = global.setTimeout(resolve, CHECKOUT_CAPTURE_TIMEOUT_MS);
        }),
      ]);
      return completed;
    } finally {
      global.clearTimeout(timeoutId);
    }
  }

  function checkoutLinkDestination(event) {
    if (
      event?.defaultPrevented ||
      (event?.button !== undefined && event.button !== 0) ||
      event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey
    ) return "";
    const link = event?.target?.closest?.("a[href]");
    if (!link || String(link.target || "").toLowerCase() === "_blank") return "";
    try {
      const url = new URL(link.href || link.getAttribute?.("href"), global.location.href);
      const checkoutPath = new URL(route("checkout"), global.location.origin).pathname.replace(/\/$/, "");
      return url.origin === global.location.origin && url.pathname.replace(/\/$/, "") === checkoutPath
        ? url.toString()
        : "";
    } catch (_error) {
      return "";
    }
  }

  function checkoutSubmitDestination(event) {
    if (event?.defaultPrevented) return "";
    const submitter = event?.submitter || event?.target?.closest?.("button, input");
    if (String(submitter?.name || "").toLowerCase() === "checkout") return route("checkout");
    try {
      const action = new URL(event?.target?.action || "", global.location.href);
      const checkoutPath = new URL(route("checkout"), global.location.origin).pathname.replace(/\/$/, "");
      return action.origin === global.location.origin && action.pathname.replace(/\/$/, "") === checkoutPath
        ? action.toString()
        : "";
    } catch (_error) {
      return "";
    }
  }

  function checkoutButtonDestination(event) {
    if (
      event?.defaultPrevented ||
      (event?.button !== undefined && event.button !== 0) ||
      event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey
    ) return "";
    const submitter = event?.target?.closest?.("button, input");
    return String(submitter?.name || "").toLowerCase() === "checkout" ? route("checkout") : "";
  }

  function acceleratedCheckoutControl(event) {
    if (
      event?.defaultPrevented ||
      (event?.button !== undefined && event.button !== 0) ||
      event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey
    ) return null;
    const path = typeof event?.composedPath === "function" ? event.composedPath() : [event?.target];
    return path.find((element) => {
      const tagName = String(element?.localName || element?.tagName || "").toLowerCase();
      const classNames = String(element?.className || "").split(/\s+/);
      return ACCELERATED_CHECKOUT_TAGS.has(tagName) ||
        element?.classList?.contains?.("shopify-payment-button") ||
        classNames.includes("shopify-payment-button");
    }) || null;
  }

  function acceleratedCheckoutAction(event, control) {
    const path = typeof event?.composedPath === "function"
      ? event.composedPath()
      : [event?.target];
    const actionable = path.find((element) => {
      if (!element || element === control || typeof element.click !== "function") return false;
      const tagName = String(element.localName || element.tagName || "").toLowerCase();
      const role = String(element.getAttribute?.("role") || "").toLowerCase();
      return ["a", "button", "input"].includes(tagName) ||
        role === "button" ||
        ACCELERATED_CHECKOUT_TAGS.has(tagName);
    });
    if (actionable) return actionable;

    const controlTag = String(control?.localName || control?.tagName || "").toLowerCase();
    if (ACCELERATED_CHECKOUT_TAGS.has(controlTag) && typeof control?.click === "function") {
      return control;
    }
    return control?.querySelector?.(
      'button, input[type="button"], input[type="submit"], a[href], [role="button"]',
    ) || null;
  }

  function checkoutCartForm(event) {
    const target = event?.target;
    const submitter = event?.submitter || target?.closest?.("button, input");
    const form = submitter?.form || (
      String(target?.tagName || target?.localName || "").toLowerCase() === "form" ||
      target?.matches?.("form")
        ? target
        : target?.closest?.("form")
    );
    const acceleratedControl = form ? null : acceleratedCheckoutControl(event);
    const drawer = acceleratedControl?.closest?.("loess-drawer") ||
      target?.closest?.("loess-drawer");
    const resolvedForm = form || drawer?.querySelector?.(
      'form[action$="/cart"], form[action$="/cart/"], #MainCartDrawer',
    );
    if (!resolvedForm) return null;
    try {
      const action = new URL(
        resolvedForm.action || resolvedForm.getAttribute?.("action") || route("cart"),
        global.location.href,
      );
      const path = action.pathname.replace(/\/$/, "");
      const localizedCartPath = new URL(route("cart"), global.location.origin).pathname.replace(/\/$/, "");
      return action.origin === global.location.origin && (path === "/cart" || path === localizedCartPath)
        ? resolvedForm
        : null;
    } catch (_error) {
      return null;
    }
  }

  function checkoutCartFormPayload(event, form) {
    const controls = Array.from(form?.elements || []);
    if (!controls.some((control) => control?.name === "note")) {
      const context = event?.target?.closest?.("loess-drawer");
      const note = context?.querySelector?.('[name="note"]');
      if (note) controls.push(note);
    }
    const updates = [];
    const attributes = {};
    let note;
    for (const control of controls) {
      if (!control?.name || control.disabled) continue;
      if (control.name === "updates[]") {
        updates.push(Math.max(0, Number.parseInt(control.value, 10) || 0));
      } else if (control.name === "note") {
        note = String(control.value || "");
      } else {
        const attribute = String(control.name).match(/^attributes\[([^\]]+)\]$/);
        if (attribute && (control.type !== "checkbox" && control.type !== "radio" || control.checked)) {
          attributes[attribute[1]] = control.value;
        }
      }
    }
    if (!updates.length && note === undefined && !Object.keys(attributes).length) return null;
    return {
      ...(updates.length ? { updates } : {}),
      ...(note === undefined ? {} : { note }),
      ...(Object.keys(attributes).length ? { attributes } : {}),
    };
  }

  async function synchronizeCheckoutCartForm(event, form, keepalive = false) {
    const payload = checkoutCartFormPayload(event, form);
    if (!payload) return false;
    const options = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      ...(keepalive ? { keepalive: true } : {}),
    };
    const response = keepalive
      ? await timedNativeRequest(
        route("cart/update.js"),
        options,
        CART_MUTATION_REQUEST_TIMEOUT_MS,
        "Accelerated checkout cart synchronization timed out.",
      )
      : await global.fetch(route("cart/update.js"), options);
    if (!response.ok) return keepalive ? null : false;
    if (!keepalive) return true;
    const { cart } = await cartSnapshotFromResponse(response);
    return cart || await readCurrentCartSnapshot();
  }

  async function settleActiveCartMutations() {
    while (activeCartMutations.size) {
      await Promise.allSettled([...activeCartMutations]);
    }
  }

  async function captureBeforeCheckout(event, destination, resumeNavigation) {
    const cartForm = checkoutCartForm(event);
    if (!destination || checkoutNavigationPending || (!cartForm && !hasPendingCapture())) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
    checkoutNavigationPending = true;
    if (cartForm) {
      try {
        await synchronizeCheckoutCartForm(event, cartForm);
      } catch (_error) {
        // Preserve the previous pending recovery if the final form sync fails.
      }
    }
    try {
      while (true) {
        await settleActiveCartMutations();
        const mutationGenerationBeforeFlush = cartMutationGeneration;
        await flushPendingCaptureBeforeCheckout(true);
        await settleActiveCartMutations();
        if (
          cartMutationGeneration === mutationGenerationBeforeFlush ||
          !hasPendingCapture()
        ) break;
      }
    } catch (_error) {
      // Checkout should remain available even if recovery capture fails.
    } finally {
      try {
        if (typeof resumeNavigation === "function") {
          resumeNavigation();
          return;
        }
      } catch (_error) {}
      global.location.assign(destination);
    }
  }

  function captureForAcceleratedCheckout(event) {
    if (acceleratedCheckoutResumeBypass) {
      acceleratedCheckoutResumeBypass = false;
      return;
    }
    if (acceleratedCheckoutFlushPromise) {
      if (acceleratedCheckoutNavigationDeferred) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
      }
      return;
    }
    const acceleratedControl = acceleratedCheckoutControl(event);
    const acceleratedAction = acceleratedCheckoutAction(event, acceleratedControl);
    const cartForm = checkoutCartForm(event);
    if (!cartForm && !hasPendingCapture(true) && !activeCartMutations.size) return;

    let navigationDeferred = false;
    let navigationResumed = false;
    const deferNavigation = () => {
      navigationDeferred = true;
      acceleratedCheckoutNavigationDeferred = true;
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
    };
    const resumeNavigation = () => {
      if (!navigationDeferred || navigationResumed) return;
      navigationResumed = true;
      acceleratedCheckoutNavigationDeferred = false;
      try {
        if (typeof acceleratedAction?.click === "function") {
          acceleratedCheckoutResumeBypass = true;
          acceleratedAction.click();
          acceleratedCheckoutResumeBypass = false;
          return;
        }
      } catch (_error) {
        acceleratedCheckoutResumeBypass = false;
      }
      global.location.assign(route("checkout"));
    };
    deferNavigation();
    acceleratedCheckoutFlushPromise = (async () => {
      await settleActiveCartMutations();
      if (cartForm) {
        try {
          const synchronizedCart = await synchronizeCheckoutCartForm(event, cartForm, true);
          if (isCompleteCartSnapshot(synchronizedCart)) {
            scheduleCapture(0, synchronizedCart);
          }
        } catch (_error) {
          // Preserve and flush any recovery that was pending before form sync.
        }
      }
      let completed = false;
      while (hasPendingCapture(true)) {
        await settleActiveCartMutations();
        if (!hasPendingCapture(true)) return completed;
        const mutationGenerationBeforeFlush = cartMutationGeneration;
        completed = await flushPendingCaptureBeforeCheckout(
          true,
          true,
          deferNavigation,
          true,
          (keepaliveStarted) => {
            if (
              keepaliveStarted &&
              cartMutationGeneration === mutationGenerationBeforeFlush
            ) resumeNavigation();
          },
          () => cartMutationGeneration === mutationGenerationBeforeFlush,
        );
        if (
          completed ||
          cartMutationGeneration === mutationGenerationBeforeFlush
        ) return completed;
      }
      return completed;
    })()
      .catch(() => {})
      .finally(() => {
        acceleratedCheckoutFlushPromise = null;
        acceleratedCheckoutNavigationDeferred = false;
        resumeNavigation();
      });
  }

  function removeRecoveryParameter() {
    const url = new URL(global.location.href);
    url.searchParams.delete(RECOVERY_PARAMETER);
    global.history.replaceState({}, "", url.toString());
  }

  function recoveryLandingElement() {
    return global.document.querySelector?.("[data-cart-recovery-landing]") || null;
  }

  function setRecoveryLandingState(state) {
    const landing = recoveryLandingElement();
    if (!landing) return false;
    landing.dataset.state = state;
    landing.setAttribute("aria-busy", state === "loading" ? "true" : "false");
    landing.querySelectorAll?.("[data-cart-recovery-state]").forEach((panel) => {
      panel.hidden = panel.dataset.cartRecoveryState !== state;
    });
    return true;
  }

  function cartHasItems(cart) {
    return Number(cart?.item_count) > 0 ||
      (Array.isArray(cart?.items) && cart.items.some((item) => Number(item?.quantity) > 0));
  }

  function restoreLineIdentity(item) {
    return JSON.stringify({
      variantId: String(positiveInteger(item?.id || item?.variant_id) || ""),
      sellingPlanId: String(positiveInteger(
        item?.sellingPlanId ||
        item?.selling_plan_id ||
        item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id,
      ) || ""),
      properties: comparableProperties(item?.properties),
    });
  }

  async function refreshedParentLineKey(
    item,
    itemsByReference,
    restoredLineKeys,
    restoredLineKeysByVariant,
  ) {
    if (!item.parentLineReference && !item.parentVariantId) return "";

    const cart = await restoreJsonRequest(route("cart.js"), {
      headers: { Accept: "application/json" },
    });
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];

    if (item.parentLineReference) {
      const parent = itemsByReference.get(item.parentLineReference);
      if (!parent) throw new Error("The saved cart has an invalid parent relationship.");
      const candidates = cartItems.filter((candidate) =>
        restoreLineIdentity(candidate) === restoreLineIdentity(parent),
      );
      const previousKey = restoredLineKeys.get(item.parentLineReference);
      const matched = candidates.find((candidate) => String(candidate?.key || "") === previousKey) ||
        (candidates.length === 1 ? candidates[0] : null);
      const refreshedKey = String(matched?.key || "");
      if (!refreshedKey) throw new Error("The restored parent line could not be identified.");

      restoredLineKeys.set(item.parentLineReference, refreshedKey);
      const variantKeys = restoredLineKeysByVariant.get(parent.id) || [];
      restoredLineKeysByVariant.set(parent.id, [
        ...new Set(variantKeys.map((key) => key === previousKey ? refreshedKey : key)),
      ]);
      return refreshedKey;
    }

    const candidates = cartItems.filter((candidate) =>
      positiveInteger(candidate?.variant_id || candidate?.id) === item.parentVariantId,
    );
    const knownKeys = new Set(restoredLineKeysByVariant.get(item.parentVariantId) || []);
    const knownCandidates = candidates.filter((candidate) => knownKeys.has(String(candidate?.key || "")));
    const matched = knownCandidates.length === 1
      ? knownCandidates[0]
      : (candidates.length === 1 ? candidates[0] : null);
    const refreshedKey = String(matched?.key || "");
    if (!refreshedKey) throw new Error("The restored parent line could not be identified.");
    restoredLineKeysByVariant.set(item.parentVariantId, [refreshedKey]);
    return refreshedKey;
  }

  async function addRestoreItems(items) {
    const pending = restorePlan(items);
    const itemsByReference = new Map(pending.map((item) => [item.lineReference, item]));
    const restoredLineKeys = new Map();
    const restoredLineKeysByVariant = new Map();

    while (pending.length) {
      let nextIndex = pending.findIndex((item) =>
        !item.parentLineReference && !item.parentVariantId,
      );
      if (nextIndex < 0) {
        nextIndex = pending.findIndex((item) =>
          item.parentLineReference && restoredLineKeys.has(item.parentLineReference),
        );
      }
      if (nextIndex < 0) {
        nextIndex = pending.findIndex((item) => {
          const variantKeys = restoredLineKeysByVariant.get(item.parentVariantId) || [];
          return !item.parentLineReference && item.parentVariantId && variantKeys.length === 1;
        });
      }
      if (nextIndex < 0) throw new Error("The saved cart has an invalid parent relationship.");

      const [item] = pending.splice(nextIndex, 1);
      const parentLineKey = await refreshedParentLineKey(
        item,
        itemsByReference,
        restoredLineKeys,
        restoredLineKeysByVariant,
      );
      if ((item.parentLineReference || item.parentVariantId) && !parentLineKey) {
        throw new Error("The saved cart has an invalid parent relationship.");
      }
      const response = await restoreJsonRequest(route("cart/add.js"), {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ items: [restoreItemPayload(item, parentLineKey)] }),
      });
      const addedItem = Array.isArray(response?.items) ? response.items[0] : response;
      const addedLineKey = String(addedItem?.key || "");
      const hasDependent = pending.some((candidate) =>
        candidate.parentLineReference === item.lineReference ||
        (!candidate.parentLineReference && candidate.parentVariantId === item.id),
      );
      if (hasDependent && !addedLineKey) {
        throw new Error("Shopify did not return the restored parent line key.");
      }
      if (addedLineKey) {
        restoredLineKeys.set(item.lineReference, addedLineKey);
        const existingVariantKeys = restoredLineKeysByVariant.get(item.id) || [];
        restoredLineKeysByVariant.set(item.id, [...existingVariantKeys, addedLineKey]);
      }
    }
  }

  async function replaceCart(targetCart, previousCart = {}) {
    const items = restorePlan(targetCart?.items);
    const discountCodes = normalizeDiscountCodes(targetCart?.discount_codes);
    const attributes = Object.fromEntries(
      Object.keys(previousCart?.attributes || {}).map((key) => [key, ""]),
    );
    Object.assign(attributes, copyProperties(targetCart?.attributes));

    await restoreJsonRequest(route("cart/clear.js"), {
      method: "POST",
      headers: { Accept: "application/json" },
    });
    if (items.length) await addRestoreItems(targetCart.items);
    const updatedCart = await restoreJsonRequest(route("cart/update.js"), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        note: targetCart?.note || "",
        attributes,
        discount: discountCodes.join(","),
      }),
    });
    const acceptedDiscountCodes = authoritativeDiscountCodes(updatedCart);
    setStoredDiscountCodes(acceptedDiscountCodes ?? discountCodes, updatedCart?.token);
    return updatedCart;
  }

  async function restoreCart(id) {
    const settings = config();
    if (!settings.apiUrl || !settings.auth || !id || restoring) {
      if (!restoring) setRecoveryLandingState("error");
      return false;
    }
    restoring = true;
    const isLandingPage = setRecoveryLandingState("loading");

    try {
      if (isLandingPage) {
        const existingCart = await withCrossTabLock(
          CART_RESTORE_LOCK,
          () => restoreJsonRequest(route("cart.js"), {
            headers: { Accept: "application/json" },
          }),
          "shared",
        );
        if (cartHasItems(existingCart)) {
          removeRecoveryParameter();
          global.location.assign(route("checkout"));
          return true;
        }
      }

      const recovery = await restoreJsonRequest(`${settings.apiUrl}/${encodeURIComponent(id)}`, {
        headers: { Accept: "application/json", auth: settings.auth },
      });
      const items = buildRestoreItems(recovery.cart?.items);
      if (!items.length) throw new Error("The saved cart is empty.");

      let preservedExistingCart = false;
      await withCrossTabLock(CART_RESTORE_LOCK, async () => {
        const currentCart = await restoreJsonRequest(route("cart.js"), {
          headers: { Accept: "application/json" },
        });
        if (isLandingPage && cartHasItems(currentCart)) {
          preservedExistingCart = true;
          return;
        }
        const currentSnapshot = buildSnapshot(currentCart);

        let restoredCart;
        try {
          restoredCart = await replaceCart(recovery.cart, currentSnapshot);
        } catch (replacementError) {
          try {
            await replaceCart(currentSnapshot, recovery.cart);
          } catch (rollbackError) {
            global.console?.error?.("Unable to roll back cart after recovery failure", rollbackError);
          }
          throw replacementError;
        }

        const restoredMarker = await markLatestCapture();
        try {
          global.localStorage?.setItem(LATEST_RESTORE_KEY, restoredMarker);
        } catch (_error) {}
        await withCrossTabLock(CAPTURE_STATE_LOCK, () => {
          if (!isLatestCapture(restoredMarker)) return;
          setLatestRecovery(id, restoredCart?.token, restoredMarker);
          setLatestTrackedCart(restoredCart, id, restoredMarker);
          clearCapturePending();
        });
      });

      removeRecoveryParameter();
      if (preservedExistingCart) {
        global.location.assign(route("checkout"));
        return true;
      }
      setRecoveryLandingState("success");
      global.location.assign(isLandingPage ? route("checkout") : `${route("cart")}?recovered=1`);
      return true;
    } catch (error) {
      global.console?.error?.("Unable to restore saved cart", error);
      if (isLandingPage) {
        setRecoveryLandingState("error");
      } else {
        removeRecoveryParameter();
        global.location.assign(`${route("cart")}?recovery_error=1`);
      }
      return false;
    } finally {
      restoring = false;
    }
  }

  const nativeFetch = global.fetch.bind(global);
  global.fetch = async function cartRecoveryFetch(input, options) {
    const cartMutation = isCartMutation(input, options);
    const performRequest = async () => {
      let previousMutationCart = null;
      const settings = config();
      if (cartMutation && settings.apiUrl && settings.auth) {
        previousMutationCart = await readCurrentCartSnapshot();
        if (previousMutationCart) {
          await ensureTrackedCartBaseline(previousMutationCart).catch(() => {});
        }
      }
      const mutationPath = cartMutation ? requestPath(input) : "";
      const mutationPayload = cartMutation ? await cartMutationPayload(input, options) : null;
      const discountCodes = cartMutation
        ? discountCodesFromMutation(input, mutationPayload)
        : null;
      const discountMutationMarker = discountCodes === null ? "" : await markLatestDiscountMutation();
      let reconciledCartSnapshot = null;
      let response;
      try {
        response = cartMutation
          ? await timedNativeRequest(
            input,
            options,
            CART_MUTATION_REQUEST_TIMEOUT_MS,
            "Cart mutation request timed out.",
          )
          : await nativeFetch(input, options);
      } catch (error) {
        if (!cartMutation || error?.name !== "CartMutationTimeoutError") throw error;
        reconciledCartSnapshot = await readCurrentCartSnapshot();
        if (!cartMutationApplied(
          mutationPath,
          mutationPayload,
          previousMutationCart,
          reconciledCartSnapshot,
        )) throw error;
        response = await reconciledCartResponse(
          mutationPath,
          mutationPayload,
          previousMutationCart,
          reconciledCartSnapshot,
        );
      }
      if (response.ok) {
        let cartSnapshot = reconciledCartSnapshot;
        let responseBodyTimedOut = false;
        if (cartMutation) {
          if (!cartSnapshot) {
            const parsedResponse = await cartSnapshotFromResponse(response);
            cartSnapshot = parsedResponse.cart;
            responseBodyTimedOut = parsedResponse.timedOut;
          }
          if (!cartSnapshot) {
            cartSnapshot = await readCurrentCartSnapshot();
          }
          if (responseBodyTimedOut && !cartSnapshot) {
            const error = new Error("Cart mutation response body timed out.");
            error.name = "CartMutationResponseTimeoutError";
            throw error;
          }
          if (responseBodyTimedOut) {
            response = await reconciledCartResponse(
              mutationPath,
              mutationPayload,
              previousMutationCart,
              cartSnapshot,
            );
          }
        }
        if (discountCodes !== null) {
          const cartToken = String(cartSnapshot?.token || "") || await responseCartToken(response);
          const acceptedDiscountCodes = authoritativeDiscountCodes(cartSnapshot);
          await storeSuccessfulDiscountMutation(
            discountMutationMarker,
            acceptedDiscountCodes ?? discountCodes,
            cartToken,
          );
        }
        if (cartMutation) {
          if (!cartSnapshot || isOversizedCapture(cartSnapshot)) {
            await captureScheduledCartWhileLocked(cartSnapshot);
          } else {
            scheduleCapture(CAPTURE_DELAY_MS, cartSnapshot);
          }
        }
      }
      return response;
    };
    if (!cartMutation) return performRequest();
    cartMutationGeneration += 1;
    const mutation = withCrossTabLock(CART_RESTORE_LOCK, performRequest);
    activeCartMutations.add(mutation);
    mutation.then(
      () => activeCartMutations.delete(mutation),
      () => activeCartMutations.delete(mutation),
    );
    return mutation;
  };

  global.document.addEventListener("cart:updated", (event) => {
    const cart = event?.detail?.cart;
    if (isOversizedCapture(cart)) {
      captureScheduledCart(cart);
    } else {
      scheduleCapture(CAPTURE_DELAY_MS, cart);
    }
  });
  global.document.addEventListener("focusin", (event) => {
    if (!checkoutCartForm(event) || currentCartSnapshot()) return;
    readCurrentCartSnapshot().then(storeCurrentCartSnapshot).catch(() => {});
  }, true);
  global.document.addEventListener("pointerdown", (event) => {
    if (acceleratedCheckoutControl(event)) captureForAcceleratedCheckout(event);
  }, true);
  global.document.addEventListener("click", (event) => {
    const acceleratedControl = acceleratedCheckoutControl(event);
    if (acceleratedControl) {
      captureForAcceleratedCheckout(event);
      return;
    }
    const destination = checkoutLinkDestination(event) || checkoutButtonDestination(event);
    const control = event?.target?.closest?.("a[href]") || event?.target?.closest?.("button, input");
    const resumeNavigation = typeof control?.click === "function" ? () => control.click() : null;
    return captureBeforeCheckout(event, destination, resumeNavigation);
  }, true);
  global.document.addEventListener("submit", (event) => {
    const form = event?.target;
    const resumeNavigation = typeof form?.requestSubmit === "function"
      ? () => form.requestSubmit(event?.submitter)
      : null;
    return captureBeforeCheckout(event, checkoutSubmitDestination(event), resumeNavigation);
  }, true);
  global.TPPCartRecovery = {
    buildSnapshot,
    buildRestoreItems,
    captureCart,
    flushPendingCaptureBeforeCheckout,
    isCartMutation,
    recoveryUrl,
    restoreCart,
  };

  const recoveryId = new URL(global.location.href).searchParams.get(RECOVERY_PARAMETER);
  const recoveryLanding = recoveryLandingElement();
  const retryButton = recoveryLanding?.querySelector?.("[data-cart-recovery-retry]");
  if (retryButton) {
    retryButton.hidden = !recoveryId;
    retryButton.addEventListener("click", () => restoreCart(recoveryId));
  }
  if (recoveryId) restoreCart(recoveryId);
  else if (recoveryLanding) setRecoveryLandingState("error");
  else if (hasPendingCapture()) scheduleCapture(0);
})(window);
